"use client";

import { APIProvider } from "@vis.gl/react-google-maps";
import { type CountryCode } from "libphonenumber-js";
import { NextIntlClientProvider, type AbstractIntlMessages } from "next-intl";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { type ReactNode } from "react";

import {
  SUPPORTED_CURRENCIES,
  type CurrencyCode,
  type SiteLocale,
} from "@trip/constants";
import { type getDatabaseExchangeRates } from "@trip/db";

import { ClientProvider } from "~/components/client-provider";
import { CurrencyProvider } from "~/components/currency-provider";
import { PHProvider } from "~/components/posthog";
import { Provider as ReactAriaProvider } from "~/components/react-aria-provider";
import { env } from "~/env";
import { getCurrencies } from "~/lib/currencies";

export function Provider({
  locale,
  exchangeRates,
  currencyPreference,
  messages,
  children,
  requestCountryCode,
}: {
  children: ReactNode;
  currencyPreference: CurrencyCode;
  exchangeRates: Awaited<ReturnType<typeof getDatabaseExchangeRates>>;
  locale: SiteLocale;
  messages: AbstractIntlMessages;
  requestCountryCode: CountryCode;
}) {
  const currencies = SUPPORTED_CURRENCIES.map((code) => ({
    ...getCurrencies(code),
    exchangeRate: exchangeRates.find(({ currency }) => code === currency)?.rate,
  }));
  currencies.unshift({ ...getCurrencies("jpy"), exchangeRate: undefined });
  const currency =
    currencies.find(({ code }) => code === currencyPreference) ?? currencies[0];

  if (!currency) {
    throw new Error("Currency not found");
  }

  return (
    <ClientProvider>
      <APIProvider apiKey={env.NEXT_PUBLIC_GOOGLE_API_KEY}>
        <ReactAriaProvider locale={locale.replace("_", "-")}>
          <NextIntlClientProvider
            locale={locale}
            messages={messages}
            now={new Date()}
            timeZone="UTC"
          >
            <CurrencyProvider
              requestCountryCode={requestCountryCode}
              currencies={currencies}
              currency={currency}
            >
              <PHProvider>
                <NuqsAdapter>{children}</NuqsAdapter>
              </PHProvider>
            </CurrencyProvider>
          </NextIntlClientProvider>
        </ReactAriaProvider>
      </APIProvider>
    </ClientProvider>
  );
}
